import * as Select from '@studio/design-system/select';
import { dayjs } from '@studio/js-utils/time';
import { useMemo, useState } from 'react';
import { RoundedProfile } from './components.jsx';
import { Expiration } from './expiration.jsx';
import type { User } from './types.js';
import { currentLimits } from '#~/api/billing/limits.js';

/** UserInviteDetails.tsx. */
export const UserInviteDetails = ({
  onDelete,
  onResend,
  onUpdate,
  user
}: {
  onDelete: () => void;
  onResend: () => void;
  onUpdate: (user: Partial<User>) => void;
  user: User;
}) => {
  const { email, expiration, permission, profilePhotoUrl, status } = user;

  const [openExpiration, setOpenExpiration] = useState(false);

  const statusUI = useMemo(() => {
    if (status === 'pending') {
      return (
        <div>
          <div className='text-left font-medium text-black text-opacity-50'>{email}</div>
          <div className='mt-4 flex gap-x-8 text-sm'>
            <div className='font-medium'>Invitation Pending</div>
            <button className='font-demibold text-primary' onClick={onResend} type='button'>
              Resend
            </button>
            <button className='font-demibold text-error' onClick={onDelete} type='button'>
              Delete
            </button>
            {/* <button
              onClick={() => {
                onUpdate({ status: 'accepted' });
              }}
              type='button'
            >
              test invitation
            </button> */}
          </div>
        </div>
      );
    }

    const isExpired = !!expiration && dayjs(expiration).isBefore(dayjs());

    return (
      <div>
        <div className='text-left'>{email}</div>
        {!!expiration && (
          <div className='mt-4 flex gap-x-4 text-sm text-secondary'>
            <div>
              {isExpired ? (
                'Access expired'
              ) : (
                <span>
                  Access expires on {dayjs(expiration).format('M/D/YYYY')} at{' '}
                  {dayjs(expiration).format('h:mma')}
                </span>
              )}
            </div>
            <button
              className='text-primary'
              onClick={() => {
                setOpenExpiration(true);
              }}
              type='button'
            >
              Edit
            </button>
            <button
              className='text-error'
              onClick={() => {
                onUpdate({ expiration: new Date(0) });
              }}
              type='button'
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }, [email, expiration, onDelete, onResend, onUpdate, status]);

  return (
    <div className='flex items-center'>
      <div className='flex grow items-center gap-x-12'>
        {profilePhotoUrl ? (
          <RoundedProfile className='border-1 border-white bg-white capitalize text-primary'>
            <img
              alt='Profile'
              className='h-full w-full rounded-full object-cover'
              src={profilePhotoUrl}
            />
          </RoundedProfile>
        ) : (
          <RoundedProfile className='border-1 border-primary bg-white capitalize text-primary'>
            {email[0]}
          </RoundedProfile>
        )}
        {statusUI}
      </div>
      {currentLimits?.limits?.canSetCollaboratorPermissions && (
        <div className='mr-2 shrink'>
          <Select.Root
            disabled={permission === 'owner'}
            onValueChange={(v) => {
              if (v === 'delete') {
                onDelete();

                return;
              } else if (v === 'expiration') {
                setOpenExpiration(true);

                return;
              }

              onUpdate({ permission: v as User['permission'] });
            }}
            value={permission}
            variant='light'
          >
            <Select.Trigger className='bg-transparent'>
              <Select.Value />
            </Select.Trigger>
            <Select.Content position='popper' side='bottom'>
              {permission === 'owner' && <Select.Item value='owner'>Owner</Select.Item>}
              <Select.Item className='h-auto' value='viewer'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Viewer</Select.ItemText>
                  <div className='text-md text-muted'>Can only view Sources & Audio</div>
                </div>
              </Select.Item>
              <Select.Item className='h-auto' value='producer'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Producer</Select.ItemText>
                  <div className='text-md text-muted'>Can manage Sources & Audio</div>
                </div>
              </Select.Item>
              <Select.Item className='h-auto' value='admin'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Admin</Select.ItemText>
                  <div className='text-md text-muted'>Can manage billing, produce, and invite</div>
                </div>
              </Select.Item>
              <Select.Item className='hover:text-white' value='expiration'>
                <div className='flex flex-col gap-4 py-8'>
                  <div>{user.expiration ? 'Edit' : 'Add'} Expiration</div>
                </div>
              </Select.Item>
              <Select.Item
                className='text-error data-highlighted:bg-error data-highlighted:text-white'
                value='delete'
              >
                <div className='flex flex-col gap-4 py-8'>
                  <div>Remove User</div>
                </div>
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      )}
      {!!openExpiration && (
        <Expiration
          initialExpiration={expiration}
          onClose={() => {
            setOpenExpiration(false);
          }}
          onDone={(date) => {
            setOpenExpiration(false);
            onUpdate({ expiration: date });
          }}
        />
      )}
    </div>
  );
};
